/**
 * WordPress dependencies
 */

import { __ } from '@wordpress/i18n';
import {
	useBlockProps,
	RichText,
	BlockControls,
	MediaPlaceholder,
	MediaUpload,
	MediaUploadCheck,
} from '@wordpress/block-editor';
import { useSelect } from '@wordpress/data';
import { ToolbarGroup, ToolbarButton } from '@wordpress/components';

/**
 * Internal dependencies
 */
import Inspector from './inspector';

/**
 * Edit function
 */

export default function Edit( { attributes, setAttributes, context } ) {
	const {
		image,
		sloganTitle,
		sloganDescription,
		config,
		enableLink,
		linkText,
		postType,
		linkedPost,
		linkBorderThickness,
		linkBorderColor,
		linkBorderRadius,
		linkPaddingTopBottom,
		linkPaddingLeftRight,
		linkMarginTop,
		linkBGColor,
		linkColor,
		linkTarget,
	} = attributes;

	// get permalink
	const postPermalink = useSelect( ( select ) => {
		const permalink = select( 'core' ).getEntityRecord(
			'postType',
			postType,
			linkedPost
		);

		return permalink?.link;
	} );

	// Block Props
	const blockProps = useBlockProps( {
		style: { borderRadius: `${ context[ 'wtrb/blockBorderRadius' ] }px` },
	} );

	return (
		<>
			{ image.url && (
				<BlockControls>
					<ToolbarGroup>
						<ToolbarButton
							onClick={ () =>
								setAttributes( {
									image: {
										url: '',
									},
								} )
							}
							icon="trash"
						/>
						<MediaUploadCheck>
							<MediaUpload
								allowedTypes={ [ 'image' ] }
								multiple={ false }
								value={ image.id }
								onSelect={ ( media ) =>
									setAttributes( {
										image: {
											url: media.url,
										},
									} )
								}
								render={ ( { open } ) => (
									<ToolbarButton
										onClick={ open }
										icon="edit"
									/>
								) }
							/>
						</MediaUploadCheck>
					</ToolbarGroup>
				</BlockControls>
			) }

			<Inspector
				attributes={ attributes }
				setAttributes={ setAttributes }
			/>

			<div { ...blockProps }>
				<div className="wtrbmr__slogan_container wtrbmr__box_shadow">
					<div className="wtrbmr__slogan_card">
						{ config === 'imagetop' && (
							<div
								className="wtrbmr__slogan_photo"
								style={ {
									height: context[ 'wtrb/contentBoxHeight' ],
								} }
							>
								{ /* IMAGE TOP */ }
								{ image && image.url ? (
									<img
										src={ image?.url }
										alt={ image?.alt }
										style={ {
											aspectRatio:
												context[
													'wtrb/imageAspectRatio'
												],
											objectFit:
												context[
													'wtrb/imageObjectFit'
												],
										} }
									/>
								) : (
									<MediaPlaceholder
										labels={ { title: 'Add photo' } }
										allowedTypes={ [ 'image' ] }
										multiple={ false }
										onSelect={ ( media ) =>
											setAttributes( {
												image: {
													id: media.id,
													url: media.url,
													alt: media.alt,
												},
											} )
										}
										onSelectURL={ ( url ) =>
											setAttributes( {
												image: {
													url: url,
												},
											} )
										}
									/>
								) }
							</div>
						) }

						<div
							className="wtrbmr__slogan_content"
							style={ {
								height: context[ 'wtrb/contentBoxHeight' ],
							} }
						>
							<div
								className="wtrbmr__slogan_content_box"
								style={ {
									padding:
										context[ 'wtrb/contentBoxPadding' ],
									textAlign:
										context[ 'wtrb/sloganTextAlignment' ],
								} }
							>
								{ /* TITLE AND DESCRIPTION */ }
								<RichText
									className="wtrbmr__slogan_title"
									tagName={ context[ 'wtrb/titleTag' ] }
									value={ sloganTitle }
									onChange={ ( newValue ) =>
										setAttributes( {
											sloganTitle: newValue,
										} )
									}
									placeholder={ __(
										'Slogan title',
										'wtrb-boxslogan-block'
									) }
									style={ {
										color: context[ 'wtrb/titleColor' ],
									} }
								/>
								<RichText
									className="wtrbmr__slogan_description"
									tagName={ context[ 'wtrb/descriptionTag' ] }
									value={ sloganDescription }
									onChange={ ( newValue ) =>
										setAttributes( {
											sloganDescription: newValue,
										} )
									}
									placeholder={ __(
										'Slogan description',
										'wtrb-boxslogan-block'
									) }
									style={ {
										color: context[
											'wtrb/descriptionColor'
										],
									} }
								/>

								{ enableLink && (
									<a
										href={ postPermalink }
										title={ linkText }
										className="wtrbmr__slogan_link"
										target={ linkTarget }
										style={ {
											border: `${ linkBorderThickness }px solid ${ linkBorderColor }`,
											borderRadius: `${ linkBorderRadius }px`,
											color: `${ linkColor }`,
											padding: `${ linkPaddingTopBottom }px ${ linkPaddingLeftRight }px`,
											marginTop: `${ linkMarginTop }px`,
											backgroundColor: `${ linkBGColor }`,
										} }
									>
										{ linkText }
									</a>
								) }
							</div>
						</div>

						{ config === 'imagebottom' && (
							<div
								className="wtrbmr__slogan_photo"
								style={ {
									height: context[ 'wtrb/contentBoxHeight' ],
								} }
							>
								{ /* IMAGE BOTTOM */ }
								{ image && image.url ? (
									<img
										src={ image?.url }
										alt={ image?.alt }
										style={ {
											aspectRatio:
												context[
													'wtrb/imageAspectRatio'
												],
											objectFit:
												context[
													'wtrb/imageObjectFit'
												],
										} }
									/>
								) : (
									<MediaPlaceholder
										labels={ { title: 'Add photo' } }
										allowedTypes={ [ 'image' ] }
										multiple={ false }
										onSelect={ ( media ) =>
											setAttributes( {
												image: {
													id: media.id,
													url: media.url,
													alt: media.alt,
												},
											} )
										}
										onSelectURL={ ( url ) =>
											setAttributes( {
												image: {
													url: url,
												},
											} )
										}
									/>
								) }
							</div>
						) }
					</div>
				</div>
			</div>
		</>
	);
}
